/**
 * BITRO Cart Override.
 *
 * Aufgaben:
 * 1) Kartendaten und Cart-HTML mit Configurator-Aufpreisen UND Mengenpreisen patchen.
 * 2) Checkout-Buttons im Cart auf den sicheren Draft-Order-Pfad umbiegen,
 *    sobald ein BITRO-Sonderpreis greifen kÃ¶nnte.
 */

(function () {
  "use strict";

  if (window.__bitroCartOverrideInstalled) return;
  window.__bitroCartOverrideInstalled = true;

  const APP_PROXY = "/apps/bitro-configurator";
  const DRAFT_ORDER_URL = APP_PROXY + "/draft-order";
  const PRICE_RULES_URL = APP_PROXY + "/price-rules";
  const CLIENT_ERROR_URL = APP_PROXY + "/client-error";
  const rawFetch = window.fetch.bind(window);

  const getStorefrontClientId = () => {
    if (window.__bitroStorefrontClientId) return window.__bitroStorefrontClientId;
    const storageKey = "bitro_storefront_client_id";
    let id = "";
    try {
      id = window.localStorage.getItem(storageKey) || "";
      if (!/^[a-zA-Z0-9_-]{12,100}$/.test(id)) {
        id = window.crypto?.randomUUID?.() ||
          `bitro_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem(storageKey, id);
      }
    } catch (_) {
      id = `bitro_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
    }
    window.__bitroStorefrontClientId = id;
    return id;
  };
  const storefrontClientId = getStorefrontClientId();

  let activeCurrencyCode = String(window.Shopify?.currency?.active || "EUR").toUpperCase();
  const setActiveCurrency = (value) => {
    const code = String(value || "").trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(code)) activeCurrencyCode = code;
  };

  let lastPatchedCart = null;
  let quantityRuleCache = new Map();
  const errorReportCache = new Map();
  const ERROR_REPORT_DEDUPE_MS = 10 * 1000;
  const SENSITIVE_PROPERTY_RE =
    /(email|e-mail|mail|telefon|phone|tel|mobile|handy|name|vorname|nachname|address|adresse|street|strasse|straße|zip|plz|city|ort|kunde|customer|note|notiz|message|nachricht)/i;

  const truncate = (value, max = 1000) => {
    const text = String(value ?? "");
    return text.length > max ? `${text.slice(0, max)}...[truncated]` : text;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const retryAfterMs = (value) => {
    if (!value) return 0;
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds > 0) return Math.min(seconds * 1000, 10000);
    const dateMs = Date.parse(value);
    return Number.isFinite(dateMs) ? Math.max(0, Math.min(dateMs - Date.now(), 10000)) : 0;
  };

  const isRetryableFetchStatus = (status) =>
    !status || status === 408 || status === 425 || status === 429 || status >= 500;

  const priceRuleRetryDelayMs = (attempt, serverDelay = 0) => {
    if (serverDelay > 0) return serverDelay;
    return [250, 700, 1500][Math.max(0, Math.min(attempt - 1, 2))];
  };

  const sanitizeProperties = (properties) => {
    if (!properties || typeof properties !== "object") return {};
    const out = {};
    for (const [key, value] of Object.entries(properties)) {
      out[key] = SENSITIVE_PROPERTY_RE.test(key) ? "[redacted]" : truncate(value, 2000);
    }
    return out;
  };

  const sanitizeCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    return {
      item_count: cart.item_count,
      total_price: cart.total_price,
      items_subtotal_price: cart.items_subtotal_price,
      total_discount: cart.total_discount,
      currency: cart.currency,
      items: cart.items.slice(0, 30).map((item) => ({
        key: truncate(item.key, 180),
        product_id: item.product_id,
        variant_id: item.variant_id,
        title: truncate(item.title || item.product_title, 300),
        product_title: truncate(item.product_title, 300),
        variant_title: truncate(item.variant_title, 300),
        quantity: item.quantity,
        price: item.price,
        final_price: item.final_price,
        line_price: item.line_price,
        final_line_price: item.final_line_price,
        original_price: item.original_price,
        original_line_price: item.original_line_price,
        properties: sanitizeProperties(item.properties),
      })),
    };
  };

  const errorPayload = (error) => ({
    name: truncate(error?.name || "Error", 120),
    message: truncate(error?.message || error, 1000),
    stack: truncate(error?.stack || "", 5000),
    status: error?.bitroStatus || null,
    response: error?.bitroResponse || null,
  });

  const reportStorefrontError = (event, error, context = {}) => {
    try {
      const payload = {
        source: "bitro-cart",
        event,
        clientId: storefrontClientId,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        error: errorPayload(error),
        context,
      };
      const cacheKey = `${event}|${window.location.pathname}|${payload.error.message}`;
      const now = Date.now();
      const last = errorReportCache.get(cacheKey) || 0;
      if (now - last < ERROR_REPORT_DEDUPE_MS) return;
      errorReportCache.set(cacheKey, now);

      const body = JSON.stringify(payload);
      const init = {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body,
      };
      if (body.length < 60000) init.keepalive = true;
      rawFetch(CLIENT_ERROR_URL, init).catch(() => {});
    } catch (_) {}
  };

  const urlOf = (input) =>
    typeof input === "string" ? input : input && input.url ? input.url : "";

  const cartPathOf = (url) =>
    typeof url === "string" ? url.split("?")[0].replace(/^https?:\/\/[^/]+/i, "") : "";

  const isCartJsonUrl = (url) => {
    const path = cartPathOf(url);
    return /\/cart\.js$/.test(path) || /\/cart\/(add|change|update|clear)\.js$/.test(path);
  };

  const isCartUrl = (url) => {
    const path = cartPathOf(url);
    return /\/cart\.js$/.test(path) || /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
  };

  const isJsonResponse = (response) =>
    /\bjson\b/i.test(String(response?.headers?.get?.("Content-Type") || ""));

  const isTransientCartRequestError = (error) => {
    const name = String(error?.name || "");
    const message = String(error?.message || "");
    return (
      /^(AbortError|TypeError|SyntaxError)$/i.test(name) &&
      /load failed|failed to fetch|networkerror|internet connection|cancelled|canceled|aborted|operation was aborted|string did not match/i.test(message)
    );
  };

  const getExplicitQuantityProductId = (item) => {
    const fromProperty = Number(item?.properties?._bitro_quantity_product_id || 0);
    if (Number.isFinite(fromProperty) && fromProperty > 0) return fromProperty;
    return 0;
  };

  const getNumericProductId = (item) => {
    const fromProperty = getExplicitQuantityProductId(item);
    if (fromProperty) return fromProperty;
    const fromCartItem = Number(item?.product_id || 0);
    if (Number.isFinite(fromCartItem) && fromCartItem > 0) return fromCartItem;
    return 0;
  };

  const getNumericVariantId = (item) => {
    const variantId = Number(item?.variant_id || 0);
    return Number.isFinite(variantId) && variantId > 0 ? Math.trunc(variantId) : 0;
  };

  const getSurchargeCents = (item) => {
    const raw = item?.properties?._bitro_surcharge_cents;
    const parsed = parseInt(raw, 10);
    return Number.isSafeInteger(parsed) ? parsed : 0;
  };

  const FBT_DISCOUNT_PERCENT = 10;
  const FBT_GROUP_RE =
    /^fbt_([1-9]\d{0,19})_([0-9a-z]{1,32})_([0-9a-z]{1,64})$/;

  const getFbtMarker = (item) => {
    const properties = item?.properties;
    if (!properties || typeof properties !== "object") return null;

    const group = String(properties._bitro_fbt_group || "").trim();
    const sourceProductId = Number(properties._bitro_fbt_source_product_id || 0);
    const role = String(properties._bitro_fbt_role || "").trim().toLowerCase();
    const groupMatch = group.match(FBT_GROUP_RE);
    if (
      !groupMatch ||
      !Number.isSafeInteger(sourceProductId) ||
      sourceProductId <= 0 ||
      Number(groupMatch[1]) !== sourceProductId ||
      (role !== "anchor" && role !== "addon")
    ) {
      return null;
    }
    return { group, sourceProductId, role };
  };

  const applyFbtDiscounts = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;

    const groups = new Map();
    for (const item of cart.items) {
      const marker = getFbtMarker(item);
      if (!marker) continue;
      const key = marker.group + "|" + marker.sourceProductId;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ item, marker });
    }

    let delta = 0;
    for (const entries of groups.values()) {
      const addons = entries.filter(({ marker }) => marker.role === "addon");
      const addonProductIds = new Set();
      let valid = addons.length >= 2;

      for (const { item, marker } of entries) {
        const productId = Number(item?.product_id || 0);
        const quantity = Number(item?.quantity || 0);
        if (!Number.isSafeInteger(productId) || productId <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) {
          valid = false;
          break;
        }
        if (marker.role === "anchor" && productId !== marker.sourceProductId) {
          valid = false;
          break;
        }
        if (marker.role === "addon") {
          if (productId === marker.sourceProductId) {
            valid = false;
            break;
          }
          addonProductIds.add(productId);
        }
      }
      if (!valid || addonProductIds.size < 2 || addonProductIds.size !== addons.length) continue;

      for (const { item, marker } of entries) {
        if (marker.role !== "addon") continue;
        // Still a set member, but an active quantity tier takes price priority.
        if (item.__bitroQuantityPricingApplied) continue;
        const quantity = Number(item.quantity);
        const currentUnit = Number(item.final_price ?? item.discounted_price ?? item.price ?? 0);
        const currentLine = Number(item.final_line_price ?? item.line_price ?? currentUnit * quantity);
        if (!Number.isFinite(currentUnit) || currentUnit <= 0 || !Number.isFinite(currentLine)) {
          continue;
        }

        // Shopify rounds the percentage discount down on the entire line.
        const discountedLine = currentLine - Math.floor((currentLine * FBT_DISCOUNT_PERCENT) / 100);
        const discountedUnit = Math.round(discountedLine / quantity);
        const itemDelta = discountedLine - currentLine;

        item.__bitroFbtDiscountApplied = true;
        item.__bitroFbtOriginalUnit = currentUnit;
        item.discounted_price = discountedUnit;
        item.final_price = discountedUnit;
        item.final_line_price = discountedLine;
        if (typeof item.presentment_price === "number") {
          item.presentment_price = discountedUnit / 100;
        }
        if (typeof item.total_discount !== "number") item.total_discount = 0;
        if (itemDelta < 0) item.total_discount += Math.abs(itemDelta);
        delta += itemDelta;
      }
    }

    if (delta !== 0) {
      if (typeof cart.items_subtotal_price === "number") cart.items_subtotal_price += delta;
      if (typeof cart.total_price === "number") cart.total_price += delta;
      if (typeof cart.total_discount !== "number") cart.total_discount = 0;
      if (delta < 0) cart.total_discount += Math.abs(delta);
    }
    return cart;
  };

  const isConfiguratorItem = (item) => {
    const properties = item?.properties;
    return !!(
      properties &&
      ("_bitro_product_id" in properties ||
        "_bitro_selections" in properties ||
        "_bitro_surcharge_cents" in properties)
    );
  };

  const getRuleCacheKey = (productIds, variantIds = []) => {
    const products = productIds
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((a, b) => a - b)
      .join(",");
    const variants = variantIds
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((a, b) => a - b)
      .join(",");
    return `p:${products}|v:${variants}`;
  };

  const convertShopCents = (shopCents, shopBaseCents, presentmentBaseCents, shopCurrency) => {
    const value = Number(shopCents);
    const shopBase = Number(shopBaseCents);
    const presentmentBase = Number(presentmentBaseCents);
    if (
      Number.isFinite(value) &&
      Number.isFinite(shopBase) &&
      Number.isFinite(presentmentBase) &&
      shopBase > 0 &&
      presentmentBase > 0
    ) {
      return Math.round((value * presentmentBase) / shopBase);
    }
    if (
      Number.isFinite(value) &&
      String(shopCurrency || "").toUpperCase() === activeCurrencyCode
    ) {
      return Math.round(value);
    }
    return null;
  };

  const chooseActiveTier = (rules, quantity) => {
    if (!Array.isArray(rules) || !Number.isFinite(quantity) || quantity <= 0) return null;
    let active = null;
    for (const rule of rules) {
      const minQuantity = Number(rule?.min_quantity ?? rule?.minQuantity ?? 0);
      const unitPriceCents = Number(rule?.unit_price_cents ?? rule?.unitPriceCents ?? 0);
      if (!Number.isFinite(minQuantity) || !Number.isFinite(unitPriceCents)) continue;
      if (quantity >= minQuantity) {
        active = { minQuantity, unitPriceCents };
      }
    }
    return active;
  };

  const getQuantityScopeKey = (productId, productRule) => {
    if (productRule?.group_id) return `group:${productRule.group_id}`;
    return productRule?.id ? `rule:${productRule.id}` : `product:${productId}`;
  };

  const quantityRuleAppliesToItem = (productRule, item) => {
    const variantIds = Array.isArray(productRule?.variant_ids)
      ? productRule.variant_ids
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      : [];
    if (variantIds.length === 0) return true;
    const variantId = getNumericVariantId(item);
    return variantId > 0 && variantIds.includes(variantId);
  };

  const getQuantityRuleSets = (productEntry) => {
    if (Array.isArray(productEntry?.rule_sets)) return productEntry.rule_sets;
    return productEntry && typeof productEntry === "object" ? [productEntry] : [];
  };

  const getQuantityRuleForItem = (productEntry, item) =>
    getQuantityRuleSets(productEntry).find(
      (rule) => rule?.isActive && quantityRuleAppliesToItem(rule, item),
    );

  const fetchQuantityRules = async (productIds, variantIds = []) => {
    const normalizedProductIds = Array.from(
      new Set(productIds.filter((id) => Number.isFinite(id) && id > 0)),
    );
    const normalizedVariantIds = Array.from(
      new Set(variantIds.filter((id) => Number.isFinite(id) && id > 0)),
    );
    if (normalizedProductIds.length === 0) {
      return { rules: {}, variantShopPrices: {}, shopCurrency: null };
    }

    const cacheKey = getRuleCacheKey(normalizedProductIds, normalizedVariantIds);
    if (quantityRuleCache.has(cacheKey)) {
      return quantityRuleCache.get(cacheKey);
    }

    const params = new URLSearchParams();
    params.set("product_id", normalizedProductIds.join(","));
    params.set("client_id", storefrontClientId);
    if (normalizedVariantIds.length > 0) {
      params.set("variant_id", normalizedVariantIds.join(","));
    }
    const url = `${PRICE_RULES_URL}?${params.toString()}`;
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const response = await rawFetch(url, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const serverDelay = retryAfterMs(response.headers?.get?.("Retry-After"));
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          const error = new Error(`Preisregeln konnten nicht geladen werden (${response.status})`);
          error.bitroStatus = response.status;
          error.bitroResponse = truncate(body, 1000);
          lastError = error;
          if (attempt < 3 && isRetryableFetchStatus(response.status)) {
            await wait(priceRuleRetryDelayMs(attempt, serverDelay));
            continue;
          }
          throw error;
        }
        const payload = await response.json();
        const result = {
          rules: payload?.rules && typeof payload.rules === "object" ? payload.rules : {},
          variantShopPrices:
            payload?.variant_shop_prices && typeof payload.variant_shop_prices === "object"
              ? payload.variant_shop_prices
              : {},
          shopCurrency: payload?.shop_currency || null,
        };
        quantityRuleCache.set(cacheKey, result);
        return result;
      } catch (error) {
        lastError = error;
        if (attempt < 3 && isRetryableFetchStatus(error?.bitroStatus || 0)) {
          await wait(priceRuleRetryDelayMs(attempt));
          continue;
        }
        break;
      }
    }
    throw lastError || new Error("Preisregeln konnten nicht geladen werden");
  };

  const applyConfiguratorSurcharge = (item) => {
    const addPerUnit = getSurchargeCents(item);
    if (addPerUnit === 0) return 0;

    const currentUnit = Number(
      item?.final_price ?? item?.price ?? item?.original_price,
    );
    if (!Number.isFinite(currentUnit) || currentUnit + addPerUnit < 0) {
      return 0;
    }

    const quantity = typeof item.quantity === "number" ? item.quantity : 1;
    const addForLine = addPerUnit * quantity;

    if (typeof item.price === "number") item.price += addPerUnit;
    if (typeof item.original_price === "number") item.original_price += addPerUnit;
    if (typeof item.discounted_price === "number") item.discounted_price += addPerUnit;
    if (typeof item.final_price === "number") item.final_price += addPerUnit;
    if (typeof item.line_price === "number") item.line_price += addForLine;
    if (typeof item.original_line_price === "number") item.original_line_price += addForLine;
    if (typeof item.final_line_price === "number") item.final_line_price += addForLine;
    if (typeof item.presentment_price === "number") item.presentment_price += addPerUnit / 100;

    return addForLine;
  };

  const applyQuantityPricing = async (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    setActiveCurrency(cart.currency);

    const productIds = new Set();
    const variantIds = new Set();
    for (const item of cart.items) {
      if (isConfiguratorItem(item)) continue;
      const productId = getNumericProductId(item);
      const variantId = getNumericVariantId(item);
      if (!productId) continue;
      productIds.add(productId);
      if (variantId) variantIds.add(variantId);
    }

    let priceRules;
    try {
      priceRules = await fetchQuantityRules(
        Array.from(productIds),
        Array.from(variantIds),
      );
    } catch (error) {
      reportStorefrontError("cart_price_rules_fetch_failed", error, {
        productIds: Array.from(productIds).slice(0, 50),
        itemCount: cart.items.length,
      });
      return cart;
    }
    const rulesByProductId = priceRules.rules;
    const scopeQuantities = new Map();
    for (const item of cart.items) {
      if (isConfiguratorItem(item)) continue;
      const productId = getNumericProductId(item);
      if (!productId) continue;
      const productRule = getQuantityRuleForItem(
        rulesByProductId[String(productId)],
        item,
      );
      if (!productRule) continue;
      const scopeKey = getQuantityScopeKey(productId, productRule);
      const quantity = Number(item.quantity) || 1;
      scopeQuantities.set(scopeKey, (scopeQuantities.get(scopeKey) || 0) + quantity);
    }

    let delta = 0;
    for (const item of cart.items) {
      if (isConfiguratorItem(item)) continue;
      const productId = getNumericProductId(item);
      if (!productId) continue;

      const productRule = getQuantityRuleForItem(
        rulesByProductId[String(productId)],
        item,
      );
      if (!productRule || !Array.isArray(productRule.rules)) continue;

      const totalQuantity = scopeQuantities.get(getQuantityScopeKey(productId, productRule)) || 0;
      const activeTier = chooseActiveTier(productRule.rules, totalQuantity);
      if (!activeTier) continue;

      const quantity = Number(item.quantity) || 1;
      const currentFinalLine = Number(item.final_line_price ?? item.line_price ?? 0);
      const currentOriginalUnit = Number(
        item.original_price ?? item.price ?? item.final_price ?? 0,
      );
      if (!Number.isFinite(currentOriginalUnit) || currentOriginalUnit <= 0) continue;

      const variantId = getNumericVariantId(item);
      const targetUnit = convertShopCents(
        activeTier.unitPriceCents,
        priceRules.variantShopPrices[String(variantId)],
        currentOriginalUnit,
        priceRules.shopCurrency,
      );
      if (!Number.isFinite(targetUnit) || targetUnit <= 0) continue;
      const targetLine = targetUnit * quantity;

      item.price = targetUnit;
      item.discounted_price = targetUnit;
      item.final_price = targetUnit;
      item.__bitroQuantityPricingApplied = true;
      item.__bitroQuantityTotalQuantity = totalQuantity;

      if (typeof item.original_price !== "number") item.original_price = currentOriginalUnit;
      if (typeof item.original_line_price !== "number") {
        item.original_line_price = currentOriginalUnit * quantity;
      }
      item.line_price = targetLine;
      item.final_line_price = targetLine;
      if (typeof item.presentment_price === "number") item.presentment_price = targetUnit / 100;

      delta += targetLine - currentFinalLine;
    }

    if (delta !== 0) {
      if (typeof cart.items_subtotal_price === "number") cart.items_subtotal_price += delta;
      if (typeof cart.total_price === "number") cart.total_price += delta;
      if (typeof cart.original_total_price === "number" && typeof cart.total_price === "number") {
        cart.original_total_price = Math.max(cart.original_total_price, cart.total_price);
      }
      if (typeof cart.total_discount !== "number") cart.total_discount = 0;
      if (delta < 0) cart.total_discount += Math.abs(delta);
    }
    return cart;
  };

  const adjustCart = async (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    setActiveCurrency(cart.currency);

    let surchargeDelta = 0;
    for (const item of cart.items) {
      surchargeDelta += applyConfiguratorSurcharge(item);
    }
    if (surchargeDelta !== 0) {
      if (typeof cart.items_subtotal_price === "number") {
        cart.items_subtotal_price = Math.max(
          0,
          cart.items_subtotal_price + surchargeDelta,
        );
      }
      if (typeof cart.total_price === "number") {
        cart.total_price = Math.max(0, cart.total_price + surchargeDelta);
      }
      if (typeof cart.original_total_price === "number") {
        cart.original_total_price = Math.max(
          0,
          cart.original_total_price + surchargeDelta,
        );
      }
      if (typeof cart.total_discount !== "number") cart.total_discount = 0;
    }

    await applyQuantityPricing(cart);
    applyFbtDiscounts(cart);
    return cart;
  };

  const fmtMoney = (cents) => {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
        style: "currency",
        currency: activeCurrencyCode,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${activeCurrencyCode}`;
    }
  };

  const MINIMUM_ORDER_NOTICE_CLASS = "bitro-minimum-order-notice";
  const MINIMUM_ORDER_STYLE_ID = "bitro-minimum-order-styles";
  const EU_COUNTRY_CODES = new Set([
    "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "DE", "EE",
    "FI", "FR", "GR", "HU", "IE", "IT", "LV", "LT", "LU",
    "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  ]);
  const INTERNATIONAL_100_COUNTRY_CODES = new Set([
    "AE", "AU", "CA", "CH", "GB", "HK", "IL",
    "JP", "KR", "MY", "NO", "NZ", "SG", "US",
  ]);
  const MINIMUM_ORDER_MESSAGES = {
    de: {
      pending:
        "Für Lieferungen außerhalb der EU gilt ein Mindestbestellwert von __MINIMUM__. Dir fehlen noch __REMAINING__.",
      reached:
        "Mindestbestellwert für Lieferungen außerhalb der EU erreicht (__MINIMUM__).",
    },
    en: {
      pending:
        "A minimum order value of __MINIMUM__ applies to deliveries outside the EU. Add __REMAINING__ more to continue.",
      reached:
        "Minimum order value for delivery outside the EU reached (__MINIMUM__).",
    },
  };
  let minimumOrderConfigLoaded = false;
  let minimumOrderConfig = null;

  const ensureMinimumOrderStyles = () => {
    if (document.getElementById(MINIMUM_ORDER_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = MINIMUM_ORDER_STYLE_ID;
    style.textContent = `
      .${MINIMUM_ORDER_NOTICE_CLASS} {
        margin-block-end: 0.75rem;
        padding: 0.75rem;
        border: 1px solid color-mix(in srgb, currentColor 24%, transparent);
        border-radius: 4px;
        background: color-mix(in srgb, currentColor 5%, transparent);
        color: inherit;
        font-size: 0.875rem;
        line-height: 1.45;
      }
      .${MINIMUM_ORDER_NOTICE_CLASS}[data-state="pending"] {
        border-color: color-mix(in srgb, #a35b00 55%, transparent);
        background: color-mix(in srgb, #f5a623 12%, transparent);
      }
      .${MINIMUM_ORDER_NOTICE_CLASS}[data-state="reached"] {
        border-color: color-mix(in srgb, #237a45 45%, transparent);
        background: color-mix(in srgb, #3aa76d 10%, transparent);
      }
    `;
    (document.head || document.documentElement).append(style);
  };

  const getMinimumOrderMessages = () => {
    const language = String(document.documentElement.lang || "en")
      .trim()
      .toLowerCase()
      .split("-")[0];
    return MINIMUM_ORDER_MESSAGES[language] || MINIMUM_ORDER_MESSAGES.en;
  };

  const getMinimumOrderConfig = () => {
    if (minimumOrderConfigLoaded) return minimumOrderConfig;
    minimumOrderConfigLoaded = true;

    const countryCode = String(window.Shopify?.country || "").trim().toUpperCase();

    // EU destinations never run minimum-order handling.
    if (!countryCode || EU_COUNTRY_CODES.has(countryCode)) return null;

    const international = INTERNATIONAL_100_COUNTRY_CODES.has(countryCode);
    const messages = getMinimumOrderMessages();
    minimumOrderConfig = {
      countryCode,
      zone: international ? "international" : "rest_of_world",
      baseCurrency: "EUR",
      minimumOrderCents: international ? 10000 : 15000,
      pendingMessage: messages.pending,
      reachedMessage: messages.reached,
    };
    ensureMinimumOrderStyles();

    return minimumOrderConfig;
  };

  const getMinimumOrderState = (cart) => {
    const config = getMinimumOrderConfig();
    if (!config || !Array.isArray(cart?.items) || cart.items.length === 0) return null;

    const subtotalCents = Number(cart.items_subtotal_price ?? cart.total_price);
    if (!Number.isFinite(subtotalCents) || subtotalCents < 0) return null;

    const cartCurrency = String(cart.currency || activeCurrencyCode).trim().toUpperCase();
    let currencyRate = 1;
    if (cartCurrency !== config.baseCurrency) {
      currencyRate = Number(window.Shopify?.currency?.rate);
      if (!Number.isFinite(currencyRate) || currencyRate <= 0) return null;
    }

    const minimumOrderCents = Math.round(config.minimumOrderCents * currencyRate);
    const remainingCents = Math.max(0, minimumOrderCents - subtotalCents);

    return {
      config,
      minimumOrderCents,
      remainingCents,
      reached: remainingCents === 0,
    };
  };

  const fillMinimumOrderMessage = (template, state) =>
    String(template || "")
      .replaceAll("__MINIMUM__", fmtMoney(state.minimumOrderCents))
      .replaceAll("__REMAINING__", fmtMoney(state.remainingCents));

  const getMinimumOrderHosts = (root) => {
    if (!root?.querySelectorAll) return [];
    const hosts = new Set();

    root.querySelectorAll(CHECKOUT_SELECTORS).forEach((trigger) => {
      const cartContext = trigger.closest?.(
        "cart-items-component, #main-cart-footer, #cart-drawer, cart-drawer, .cart-drawer, .cart-form",
      );
      if (!cartContext) return;

      const host = trigger.closest?.(".cart__ctas") || trigger.parentElement;
      if (host) hosts.add(host);
    });

    return Array.from(hosts);
  };

  const getDirectMinimumOrderNotice = (host) =>
    Array.from(host?.children || []).find((child) =>
      child.classList?.contains(MINIMUM_ORDER_NOTICE_CLASS),
    ) || null;

  const renderMinimumOrderNotices = (root, cart) => {
    if (!root?.querySelectorAll) return null;

    const state = getMinimumOrderState(cart);
    const existingNotices = Array.from(
      root.querySelectorAll(`.${MINIMUM_ORDER_NOTICE_CLASS}`),
    );
    if (!state) {
      existingNotices.forEach((notice) => notice.remove());
      return null;
    }

    const activeNotices = new Set();
    getMinimumOrderHosts(root).forEach((host) => {
      let notice = getDirectMinimumOrderNotice(host);
      if (!notice) {
        notice = document.createElement("div");
        notice.className = MINIMUM_ORDER_NOTICE_CLASS;
        notice.setAttribute("role", "status");
        notice.setAttribute("aria-live", "polite");
        host.prepend(notice);
      }

      notice.dataset.state = state.reached ? "reached" : "pending";
      const template = state.reached
        ? state.config.reachedMessage
        : state.config.pendingMessage;
      notice.textContent = fillMinimumOrderMessage(template, state);
      activeNotices.add(notice);
    });

    existingNotices.forEach((notice) => {
      if (!activeNotices.has(notice)) notice.remove();
    });
    return state;
  };

  const getClosestContext = (node) =>
    node?.closest?.(".shopify-section, dialog, product-card, section") || document;

  const getQuantityPricingRows = (container) =>
    Array.from(container?.querySelectorAll?.("[data-min-quantity]") || []);

  const getQuantityPricingState = (container) => {
    if (!container?.dataset) return null;

    const basePriceCents = Number(container.dataset.basePriceCents || 0);
    if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) return null;

    const rules = getQuantityPricingRows(container)
      .map((row) => ({
        row,
        minQuantity: Number(row.dataset.minQuantity || 0),
        unitPriceCents: Number(row.dataset.unitPriceCents || 0),
        discountPercent: Number(row.dataset.discountPercent || 0),
      }))
      .filter(
        (rule) =>
          Number.isFinite(rule.minQuantity) &&
          rule.minQuantity > 0 &&
          Number.isFinite(rule.unitPriceCents) &&
          rule.unitPriceCents > 0,
      )
      .sort((a, b) => a.minQuantity - b.minQuantity);

    return {
      productId: String(container.dataset.productId || ""),
      basePriceCents,
      rules,
    };
  };

  const hydrateQuantityPricingContainer = async (container) => {
    if (!container?.dataset) return false;
    setActiveCurrency(container.dataset.currencyCode);
    const requiresConversion = container.dataset.requiresConversion === "true";
    if (!requiresConversion) {
      container.dataset.pricingReady = "true";
      return true;
    }

    const productId = Number(container.dataset.productId || 0);
    const variantId = Number(container.dataset.variantId || 0);
    const presentmentBase = Number(container.dataset.basePriceCents || 0);
    if (!productId || !variantId || !presentmentBase) return false;

    try {
      const payload = await fetchQuantityRules([productId], [variantId]);
      const shopBase = payload.variantShopPrices[String(variantId)];
      if (!Number.isFinite(shopBase) || shopBase <= 0) {
        throw new Error("Shop-Basispreis fuer Mengenpreis fehlt");
      }

      getQuantityPricingRows(container).forEach((row) => {
        const shopUnit = Number(
          row.dataset.shopUnitPriceCents || row.dataset.unitPriceCents || 0,
        );
        row.dataset.shopUnitPriceCents = String(shopUnit);
        const converted = convertShopCents(
          shopUnit,
          shopBase,
          presentmentBase,
          payload.shopCurrency,
        );
        if (!Number.isFinite(converted) || converted <= 0) return;
        const discount = Math.max(
          0,
          Math.round(((presentmentBase - converted) * 100) / presentmentBase),
        );
        row.dataset.unitPriceCents = String(converted);
        row.dataset.discountPercent = String(discount);
        const price = row.querySelector(".bitro-quantity-pricing__price");
        const badge = row.querySelector(".bitro-quantity-pricing__badge");
        if (price) price.textContent = `${fmtMoney(converted)}/Stk`;
        if (badge) badge.textContent = `-${discount}% Rabatt`;
      });
      container.dataset.pricingReady = "true";
      return true;
    } catch (error) {
      reportStorefrontError("quantity_pricing_conversion_failed", error, {
        productId,
        variantId,
        currency: activeCurrencyCode,
      });
      container.remove();
      return false;
    }
  };

  const getProductPriceElementForContainer = (container) => {
    const productId = container?.dataset?.productId;
    if (!productId) return null;
    const context = getClosestContext(container);
    return (
      context.querySelector(`product-price[data-product-id="${productId}"]`) ||
      document.querySelector(`product-price[data-product-id="${productId}"]`)
    );
  };

  const resetProductPriceFromUpdatedHtml = (container, updatedHtml, productId) => {
    const priceElement = getProductPriceElementForContainer(container);
    const priceContainer = priceElement?.querySelector?.('[ref="priceContainer"]');
    if (!priceContainer) return;

    const replacementPriceContainer = updatedHtml
      ?.querySelector?.(`product-price[data-product-id="${productId}"]`)
      ?.querySelector?.('[ref="priceContainer"]');

    if (replacementPriceContainer) {
      priceContainer.innerHTML = replacementPriceContainer.innerHTML;
      if (priceElement) priceElement.__bitroOriginalPriceMarkup = priceContainer.innerHTML;
      return;
    }

    if (typeof priceElement?.__bitroOriginalPriceMarkup === "string") {
      priceContainer.innerHTML = priceElement.__bitroOriginalPriceMarkup;
    }
  };

  const getQuantityInputForContainer = (container) =>
    container
      ?.closest?.("product-form-component")
      ?.querySelector?.('input[name="quantity"][ref="quantityInput"]');

  const getCurrentQuantityForContainer = (container) => {
    const input = getQuantityInputForContainer(container);
    const quantity = Number.parseInt(input?.value || "1", 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const captureOriginalProductPriceMarkup = (priceElement) => {
    if (!priceElement?.querySelector) return null;
    const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return null;
    if (!priceContainer.querySelector(".bitro-quantity-price__sale")) {
      priceElement.__bitroOriginalPriceMarkup = priceContainer.innerHTML;
    }
    return priceContainer;
  };

  const highlightActiveQuantityTier = (container, activeTier) => {
    getQuantityPricingRows(container).forEach((row) => {
      const rowMinQuantity = Number(row.dataset.minQuantity || 0);
      row.classList.toggle(
        "bitro-quantity-pricing__row--active",
        !!activeTier && rowMinQuantity === activeTier.minQuantity,
      );
    });
  };

  const renderQuantityTierProductPrice = (state, activeTier) => {
    const discountPercent = Number.isFinite(activeTier.discountPercent)
      ? activeTier.discountPercent
      : Math.max(
          0,
          Math.round(
            ((state.basePriceCents - activeTier.unitPriceCents) * 100) / state.basePriceCents,
          ),
        );

    return `
      <div class="bitro-quantity-price__sale">
        <span class="price-item__group">
          <span class="visually-hidden">Regular price</span>
          <span class="price-item--regular compare-at-price">${fmtMoney(state.basePriceCents)}</span>
        </span>
        <span class="price-item__group">
          <span class="visually-hidden">Sale price</span>
          <span class="price-item--sale price">${fmtMoney(activeTier.unitPriceCents)}</span>
        </span>
        <span class="bitro-quantity-price__badge">-${discountPercent}% Rabatt</span>
      </div>
    `;
  };

  const syncProductQuantityPricing = (container, quantity = null) => {
    const state = getQuantityPricingState(container);
    if (!state || state.rules.length === 0) return;

    const resolvedQuantity =
      Number.isFinite(quantity) && quantity > 0
        ? quantity
        : getCurrentQuantityForContainer(container);
    const activeTier = chooseActiveTier(state.rules, resolvedQuantity);
    highlightActiveQuantityTier(container, activeTier);

    const priceElement = getProductPriceElementForContainer(container);
    if (!priceElement) return;

    const priceContainer = captureOriginalProductPriceMarkup(priceElement);
    if (!priceContainer) return;

    if (
      !activeTier ||
      !Number.isFinite(activeTier.unitPriceCents) ||
      activeTier.unitPriceCents >= state.basePriceCents
    ) {
      if (typeof priceElement.__bitroOriginalPriceMarkup === "string") {
        priceContainer.innerHTML = priceElement.__bitroOriginalPriceMarkup;
      }
      return;
    }

    priceContainer.innerHTML = renderQuantityTierProductPrice(state, activeTier);
  };

  const syncAllProductQuantityPricing = (scope = document) => {
    scope
      .querySelectorAll?.("[data-bitro-quantity-pricing]")
      .forEach((container) => syncProductQuantityPricing(container));
  };

  const initializeProductQuantityPricingContainer = async (container) => {
    const priceElement = getProductPriceElementForContainer(container);
    const priceContainer = priceElement?.querySelector?.('[ref="priceContainer"]');

    if (priceElement && priceContainer) {
      priceElement.__bitroOriginalPriceMarkup = priceContainer.innerHTML;
    }

    if (await hydrateQuantityPricingContainer(container)) {
      syncProductQuantityPricing(container);
    }
  };

  const clearQuantityPricingSlot = (slot) => {
    if (!slot?.querySelectorAll) return;
    slot.querySelectorAll("[data-bitro-quantity-pricing]").forEach((container) => {
      container.remove();
    });
  };

  const PRICE_SELECTORS_ROW = [
    ".price",
    ".money",
    "[data-cart-item-price]",
    ".cart-item__price",
    ".cart-item__final-price",
    ".cart-item__line-price",
    ".cart-items__price",
    ".cart-item-price",
    ".line-item__price",
    ".line-item__total",
  ].join(",");

  const SUBTOTAL_SELECTORS = [
    "[data-cart-subtotal]",
    ".cart__subtotal .money",
    ".totals__subtotal-value",
    "#main-cart-footer .totals__subtotal-value",
    ".cart__subtotal-container .money",
  ].join(",");

  const TOTAL_SELECTORS = [
    "[data-cart-total]",
    ".cart__total .money",
    ".totals__total-value",
    "#main-cart-footer .totals__total-value",
    ".cart__total-value",
    ".cart__total-container .money",
  ].join(",");

  const setPriceText = (el, text) => {
    if (!el) return;
    el.textContent = text;
    if (el.tagName && el.tagName.toLowerCase() === "text-component" && el.setAttribute) {
      el.setAttribute("value", text);
    }
  };

  const getDiscountPercent = (originalUnit, finalUnit) => {
    if (
      !Number.isFinite(originalUnit) ||
      !Number.isFinite(finalUnit) ||
      originalUnit <= 0 ||
      finalUnit >= originalUnit
    ) {
      return 0;
    }

    return Math.max(0, Math.round(((originalUnit - finalUnit) * 100) / originalUnit));
  };

  const buildCartUnitPriceMarkup = (item) => {
    const originalUnit = Number(
      item.__bitroFbtDiscountApplied
        ? item.__bitroFbtOriginalUnit
        : item.original_price ?? item.price ?? item.final_price ?? 0,
    );
    const finalUnit = Number(item.final_price ?? item.price ?? 0);

    if (!Number.isFinite(finalUnit) || finalUnit <= 0) return "";

    if (item.__bitroFbtDiscountApplied && finalUnit < originalUnit) {
      return `
        <span class="bitro-cart-unit-price">
          <s class="compare-at-price">${fmtMoney(originalUnit)}</s>
          <span class="bitro-cart-unit-price__current">${fmtMoney(finalUnit)}</span>
          <span class="bitro-cart-unit-price__badge">-10% Bundle</span>
        </span>
      `;
    }

    if (item.__bitroQuantityPricingApplied && finalUnit < originalUnit) {
      const discountPercent = getDiscountPercent(originalUnit, finalUnit);
      return `
        <span class="bitro-cart-unit-price">
          <s class="compare-at-price">${fmtMoney(originalUnit)}</s>
          <span class="bitro-cart-unit-price__current">${fmtMoney(finalUnit)}</span>
          <span class="bitro-cart-unit-price__badge">-${discountPercent}% Rabatt</span>
        </span>
      `;
    }

    return fmtMoney(finalUnit !== originalUnit && finalUnit > 0 ? finalUnit : originalUnit);
  };

  const escapeSelectorValue = (value) => {
    const string = String(value ?? "");
    if (window.CSS?.escape) return window.CSS.escape(string);
    return string.replace(/["\\]/g, "\\$&");
  };

  const getRowsFromMatches = (matches) => {
    const rows = new Set();
    matches.forEach((match) => {
      if (!(match instanceof Element)) return;
      const row = match.matches(".cart-items__table-row")
        ? match
        : match.closest(".cart-items__table-row");
      if (row) rows.add(row);
    });
    return Array.from(rows);
  };

  const getCartRowsForItem = (root, item) => {
    const q = (sel) => (root.querySelectorAll ? root.querySelectorAll(sel) : []);
    const key = escapeSelectorValue(item?.key);
    if (key) {
      const keyRows = getRowsFromMatches(q(`[data-key="${key}"],[data-line-item-key="${key}"]`));
      if (keyRows.length > 0) return keyRows;
    }

    const variantId = escapeSelectorValue(item?.variant_id);
    if (!variantId) return [];
    return getRowsFromMatches(
      q(`[data-variant-id="${variantId}"],[data-cart-item-variant-id="${variantId}"]`),
    );
  };

  const rewritePricesInRoot = (root, cart) => {
    if (!root || !cart || !Array.isArray(cart.items)) return;
    const q = (sel) => (root.querySelectorAll ? root.querySelectorAll(sel) : []);

    for (const item of cart.items) {
      const rows = getCartRowsForItem(root, item);
      const lineText = fmtMoney(item.final_line_price ?? item.line_price);
      const previewUrl = item.properties && item.properties._bitro_preview_image;
      rows.forEach((row) => {
        row.querySelectorAll(PRICE_SELECTORS_ROW).forEach((el) => setPriceText(el, lineText));
        row.querySelectorAll("text-component").forEach((el) => setPriceText(el, lineText));
        const unitPriceContainer = row.querySelector(".cart-items__unit-price-wrapper > div");
        if (unitPriceContainer) {
          unitPriceContainer.innerHTML = buildCartUnitPriceMarkup(item);
        }
        if (previewUrl && typeof previewUrl === "string") {
          row
            .querySelectorAll(".cart-items__media-image, .cart-item__image, .cart-item__media img, [data-cart-item-image]")
            .forEach((img) => {
              if (img.getAttribute("src") !== previewUrl) {
                img.setAttribute("src", previewUrl);
                img.removeAttribute("srcset");
                img.removeAttribute("data-src");
                img.removeAttribute("data-srcset");
              }
            });
        }
      });
    }

    if (typeof cart.items_subtotal_price === "number") {
      const subText = fmtMoney(cart.items_subtotal_price);
      q(SUBTOTAL_SELECTORS).forEach((el) => setPriceText(el, subText));
    }
    if (typeof cart.total_price === "number") {
      const totText = fmtMoney(cart.total_price);
      q(TOTAL_SELECTORS).forEach((el) => setPriceText(el, totText));
    }

    renderMinimumOrderNotices(root, cart);
  };

  const patchSectionsHTML = (data) => {
    if (!data || !data.sections || typeof data.sections !== "object") return;
    for (const key of Object.keys(data.sections)) {
      const html = data.sections[key];
      if (typeof html !== "string" || !html) continue;
      try {
        const doc = new DOMParser().parseFromString(
          `<template id="__bitro_wrap">${html}</template>`,
          "text/html",
        );
        const tpl = doc.getElementById("__bitro_wrap");
        if (!tpl || !tpl.content) continue;
        rewritePricesInRoot(tpl.content, data);
        const serializer = document.createElement("div");
        serializer.appendChild(tpl.content.cloneNode(true));
        data.sections[key] = serializer.innerHTML;
      } catch (error) {
        reportStorefrontError("cart_sections_html_patch_failed", error, { sectionKey: key, cart: sanitizeCart(data) });
      }
    }
  };

  const patchPayload = async (data) => {
    if (!data || typeof data !== "object") return data;
    if (Array.isArray(data.items)) {
      await adjustCart(data);
      lastPatchedCart = data;
      patchSectionsHTML(data);
    } else if ("variant_id" in data || "properties" in data) {
      applyConfiguratorSurcharge(data);
    } else if (Array.isArray(data.items)) {
      for (const item of data.items) {
        applyConfiguratorSurcharge(item);
      }
    }
    return data;
  };

  window.fetch = async function (input, init) {
    const response = await rawFetch(input, init);
    const url = urlOf(input);
    if (!isCartUrl(url)) return response;
    if (!isCartJsonUrl(url) && !isJsonResponse(response)) return response;

    try {
      const payload = await response.clone().json();
      await patchPayload(payload);
      const patchedHeaders = new Headers(response.headers);
      patchedHeaders.delete("content-encoding");
      patchedHeaders.delete("content-length");
      patchedHeaders.delete("transfer-encoding");
      patchedHeaders.set("content-type", "application/json; charset=utf-8");
      return new Response(JSON.stringify(payload), {
        status: response.status,
        headers: patchedHeaders,
      });
    } catch (error) {
      reportStorefrontError("cart_fetch_patch_failed", error, {
        cartUrl: url,
        contentType: response.headers?.get?.("Content-Type") || "",
        status: response.status,
      });
      return response;
    }
  };

  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '[href$="/checkout"]',
    '[href*="/checkouts/"]',
  ].join(",");

  let checkoutInProgress = false;

  const getCheckoutFormForTrigger = (triggerEl) => {
    const localForm = triggerEl?.closest?.("cart-items-component")?.querySelector?.("form.cart-form");
    if (localForm instanceof HTMLFormElement) return localForm;
    if (triggerEl?.form instanceof HTMLFormElement) return triggerEl.form;
    const formId = triggerEl?.getAttribute?.("form");
    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) return form;
    }
    return (
      triggerEl?.closest?.("form") ||
      document.querySelector("form.cart-form, form[action='/cart'], form[action$='/cart']")
    );
  };

  const getVisibleCartUpdates = (triggerEl) => {
    const form = getCheckoutFormForTrigger(triggerEl);
    if (!form?.querySelectorAll) return null;

    const inputs = Array.from(form.querySelectorAll('input[name="updates[]"]'));
    if (inputs.length === 0) return null;

    const updates = [];
    inputs.forEach((input, index) => {
      const line = Number(input.getAttribute("data-cart-line") || index + 1);
      if (!Number.isFinite(line) || line <= 0) return;
      const min = Number(input.getAttribute("min") || input.getAttribute("data-min") || 0);
      const rawQuantity = Number.parseInt(input.value || "0", 10);
      const quantity = Number.isFinite(rawQuantity) ? Math.max(Number.isFinite(min) ? min : 0, rawQuantity) : 0;
      updates[line - 1] = quantity;
    });

    return updates.length > 0 ? updates.map((quantity) => (Number.isFinite(quantity) ? quantity : 0)) : null;
  };

  const getVisibleCartCheckoutProblems = (triggerEl) => {
    const form = getCheckoutFormForTrigger(triggerEl);
    if (!form?.querySelectorAll) return [];

    const problems = [];
    form.querySelectorAll('input[name="updates[]"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.disabled) return;

      const value = Number.parseInt(input.value || "0", 10);
      const min = Number(input.getAttribute("min") || input.getAttribute("data-min") || 1);
      const maxAttr = input.getAttribute("max");
      const max = maxAttr == null || maxAttr === "" ? null : Number(maxAttr);

      if (!Number.isFinite(value) || value < min) {
        problems.push("Eine Warenkorb-Menge ist ungueltig.");
        input.reportValidity?.();
        return;
      }

      if (max !== null && Number.isFinite(max) && value > max) {
        problems.push(`Eine Warenkorb-Menge liegt ueber dem erlaubten Maximum (${max}).`);
        input.reportValidity?.();
      } else if (typeof input.checkValidity === "function" && !input.checkValidity()) {
        problems.push("Eine Warenkorb-Menge ist ungueltig.");
        input.reportValidity?.();
      }
    });

    const cartRoot = form.closest("cart-items-component") || document;
    cartRoot
      .querySelectorAll(".cart-items__error:not(.hidden), .cart-item__error:not(.hidden)")
      .forEach((errorContainer) => {
        const text = (errorContainer.textContent || "").trim();
        if (text) problems.push(text);
      });

    return Array.from(new Set(problems));
  };

  const blockCheckoutForCartProblem = (message) => {
    const error = new Error(
      message ||
        "Bitte pruefe den Warenkorb erneut. Eine Menge wurde angepasst oder ist nicht verfuegbar.",
    );
    error.bitroCartCheckoutBlocked = true;
    throw error;
  };

  const getCartQuantityAdjustments = (updates, cart) => {
    if (!Array.isArray(updates) || !Array.isArray(cart?.items)) return [];

    const adjustments = [];
    updates.forEach((requestedQuantity, index) => {
      if (!Number.isFinite(requestedQuantity)) return;
      const item = cart.items[index];
      const actualQuantity = item ? Number(item.quantity) || 0 : 0;
      if (actualQuantity === requestedQuantity) return;

      adjustments.push({
        line: index + 1,
        title: item?.product_title || item?.title || "Produkt",
        requestedQuantity,
        actualQuantity,
      });
    });
    return adjustments;
  };

  const buildCartQuantityAdjustedMessage = (adjustments) => {
    if (!adjustments.length) {
      return "Eine Menge wurde an den verfuegbaren Lagerbestand angepasst. Bitte pruefe den Warenkorb erneut.";
    }

    const first = adjustments[0];
    const suffix = adjustments.length > 1 ? ` und ${adjustments.length - 1} weitere Position(en)` : "";
    return `${first.title}${suffix}: Die Menge wurde von ${first.requestedQuantity} auf ${first.actualQuantity} angepasst. Bitte pruefe den Warenkorb erneut.`;
  };

  const visibleUpdatesMatchCart = (updates, cart) => {
    if (!Array.isArray(updates) || !Array.isArray(cart?.items)) return false;
    if (updates.length !== cart.items.length) return false;
    return updates.every((requestedQuantity, index) => {
      if (!Number.isFinite(requestedQuantity)) return false;
      const actualQuantity = Number(cart.items[index]?.quantity || 0);
      return actualQuantity === requestedQuantity;
    });
  };

  const isCartItemsChangedMessage = (status, message) =>
    status === 422 && /artikel wurden geaendert|artikel wurden geändert|items? (?:were|was|have been|has been)? ?changed|cart (?:was|has been)? ?changed/i.test(String(message || ""));

  const loadFreshCartForQuantitySync = async () => {
    const response = await rawFetch("/cart.js", { credentials: "include" });
    if (!response.ok) return null;
    return response.json().catch(() => null);
  };

  const syncVisibleCartQuantities = async (triggerEl) => {
    const updates = getVisibleCartUpdates(triggerEl);
    if (!updates) return;
    if (visibleUpdatesMatchCart(updates, lastPatchedCart)) return;

    if (!lastPatchedCart) {
      const freshCart = await loadFreshCartForQuantitySync().catch(() => null);
      if (freshCart && visibleUpdatesMatchCart(updates, freshCart)) {
        lastPatchedCart = freshCart;
        return;
      }
    }

    let response;
    try {
      response = await rawFetch("/cart/update.js", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
        body: JSON.stringify({ updates }),
      });
    } catch (error) {
      const freshCart = isTransientCartRequestError(error)
        ? await loadFreshCartForQuantitySync().catch(() => null)
        : null;
      if (freshCart && visibleUpdatesMatchCart(updates, freshCart)) {
        lastPatchedCart = freshCart;
        scheduleRewrite({ delay: 40 });
        return;
      }
      throw error;
    }

    let cart = null;
    let responseText = "";
    try {
      cart = await response.clone().json();
    } catch {
      responseText = await response.clone().text().catch(() => "");
    }

    if (!response.ok) {
      const message =
        cart?.description ||
        cart?.message ||
        responseText ||
        "Warenkorb-Mengen konnten nicht aktualisiert werden. Bitte pruefe die verfuegbare Menge.";

      if (isCartItemsChangedMessage(response.status, message)) {
        const freshCart = await loadFreshCartForQuantitySync();
        if (freshCart && visibleUpdatesMatchCart(updates, freshCart)) {
          lastPatchedCart = null;
          scheduleRewrite({ fresh: true, delay: 40 });
          return;
        }
        const adjustments = getCartQuantityAdjustments(updates, freshCart);
        if (adjustments.length > 0) {
          lastPatchedCart = null;
          scheduleRewrite({ fresh: true, delay: 40 });
          const changedError = new Error(buildCartQuantityAdjustedMessage(adjustments));
          changedError.bitroCartQuantityAdjusted = true;
          throw changedError;
        }
      }

      const error = new Error(message);
      error.bitroStatus = response.status;
      error.bitroResponse = cart || truncate(responseText, 1000);
      throw error;
    }

    const adjustments = getCartQuantityAdjustments(updates, cart);
    if (adjustments.length > 0) {
      lastPatchedCart = null;
      scheduleRewrite({ fresh: true, delay: 40 });

      const error = new Error(buildCartQuantityAdjustedMessage(adjustments));
      error.bitroCartQuantityAdjusted = true;
      throw error;
    }

    lastPatchedCart = null;
  };

  const startDraftOrderCheckout = async (triggerEl, syncedCart = null) => {
    if (checkoutInProgress) return;
    checkoutInProgress = true;
    const originalLabel = triggerEl && triggerEl.textContent;
    let reportCart = null;

    if (triggerEl && "disabled" in triggerEl) triggerEl.disabled = true;
    if (triggerEl) triggerEl.textContent = "Bestellung wird vorbereitet...";

    try {
      const rawCart = syncedCart || (await (async () => {
        await syncVisibleCartQuantities(triggerEl);
        const rawCartResponse = await rawFetch("/cart.js", { credentials: "include" });
        if (!rawCartResponse.ok) throw new Error("Cart konnte nicht geladen werden");
        return rawCartResponse.json();
      })());
      reportCart = rawCart;
      if (!rawCart.items || rawCart.items.length === 0) {
        throw new Error("Warenkorb ist leer");
      }

      const draftOrderUrl =
        `${DRAFT_ORDER_URL}?client_id=${encodeURIComponent(storefrontClientId)}`;
      const response = await rawFetch(draftOrderUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: rawCart.items,
          currency: rawCart.currency,
          clientId: storefrontClientId,
        }),
      });
      const data = await response.json();
      const checkoutUrl = data.invoiceUrl || data.checkoutUrl;
      if (!response.ok || !checkoutUrl) {
        const checkoutError = new Error(data.error || "Fehler bei Draft-Order-Erstellung");
        checkoutError.bitroStatus = response.status;
        checkoutError.bitroResponse = data;
        throw checkoutError;
      }
      window.location.href = checkoutUrl;
    } catch (error) {
      reportStorefrontError("cart_checkout_failed", error, { cart: sanitizeCart(reportCart), triggerText: truncate(originalLabel, 120) });
      checkoutInProgress = false;
      if (triggerEl && "disabled" in triggerEl) triggerEl.disabled = false;
      if (triggerEl && originalLabel) triggerEl.textContent = originalLabel;
      throw error;
    }
  };

  let checkoutPreparing = false;

  const onCheckoutClick = (event) => {
    const target = event.target.closest(CHECKOUT_SELECTORS);
    if (!target) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    if (checkoutPreparing || checkoutInProgress) return;
    checkoutPreparing = true;

    (async () => {
      const visibleProblems = getVisibleCartCheckoutProblems(target);
      if (visibleProblems.length > 0) {
        blockCheckoutForCartProblem(visibleProblems[0]);
      }

      let quantitySyncTransientError = null;
      try {
        await syncVisibleCartQuantities(target);
      } catch (error) {
        if (!isTransientCartRequestError(error)) throw error;
        quantitySyncTransientError = error;
        reportStorefrontError("cart_quantity_sync_transient_continue", error, {
          cart: sanitizeCart(lastPatchedCart),
        });
      }

      const problemsAfterSync = getVisibleCartCheckoutProblems(target);
      if (problemsAfterSync.length > 0) {
        blockCheckoutForCartProblem(problemsAfterSync[0]);
      }

      let response = null;
      let cart = null;
      try {
        response = await rawFetch("/cart.js", { credentials: "include" });
        if (response.ok) {
          cart = await response.json();
        }
      } catch (error) {
        if (!isTransientCartRequestError(error)) throw error;
        reportStorefrontError("cart_checkout_cart_fetch_transient_continue", error, {
          cart: sanitizeCart(lastPatchedCart),
          quantitySyncHadTransientError: !!quantitySyncTransientError,
        });
      }

      if (!cart && lastPatchedCart?.items?.length) {
        cart = lastPatchedCart;
      }

      if (!cart) {
        const statusSuffix = response ? ` (HTTP ${response.status})` : "";
        throw (
          quantitySyncTransientError ||
          new Error(`Cart konnte nicht geladen werden${statusSuffix}`)
        );
      }

      await startDraftOrderCheckout(target, cart);
    })().catch((error) => {
      const quantityAdjusted = !!error?.bitroCartQuantityAdjusted;
      const checkoutBlocked = !!error?.bitroCartCheckoutBlocked;
      if (!quantityAdjusted && !checkoutBlocked) {
        reportStorefrontError("cart_checkout_preparation_failed", error, { cart: sanitizeCart(lastPatchedCart), quantityAdjusted, checkoutBlocked });
      }
      checkoutPreparing = false;
      const message =
        error && error.message
          ? error.message
          : "Bitte pruefe den Warenkorb erneut. Eine Menge wurde angepasst oder ist nicht verfuegbar.";
      alert(message);

      if (error?.bitroCartQuantityAdjusted || error?.bitroCartCheckoutBlocked) {
        window.location.href = "/cart";
      }
    });
  };

  document.addEventListener("click", onCheckoutClick, true);

  const rewriteCartDom = async (options = {}) => {
    try {
      let cart = options.fresh ? null : lastPatchedCart;
      if (!cart) {
        const response = await rawFetch("/cart.js", { credentials: "include" });
        if (!response.ok) return;
        cart = await response.json();
        await adjustCart(cart);
        lastPatchedCart = cart;
      }
      rewritePricesInRoot(document, cart);
    } catch (error) {
      if (!isTransientCartRequestError(error) || lastPatchedCart) {
        reportStorefrontError("cart_dom_rewrite_failed", error, { cart: sanitizeCart(lastPatchedCart) });
      }
    }
  };

  let rewriting = false;
  let rewritePending = false;
  let rewriteNeedsFreshCart = false;
  const scheduleRewrite = (options = {}) => {
    if (options.fresh) {
      lastPatchedCart = null;
      rewriteNeedsFreshCart = true;
    }
    if (rewriting) {
      rewritePending = true;
      return;
    }
    if (rewritePending) return;
    rewritePending = true;
    setTimeout(async () => {
      rewritePending = false;
      const fresh = rewriteNeedsFreshCart;
      rewriteNeedsFreshCart = false;
      rewriting = true;
      try {
        await rewriteCartDom({ fresh });
      } finally {
        setTimeout(() => {
          const shouldRunAgain = rewritePending || rewriteNeedsFreshCart;
          const freshAgain = rewriteNeedsFreshCart;
          rewritePending = false;
          rewriteNeedsFreshCart = false;
          rewriting = false;
          if (shouldRunAgain) {
            scheduleRewrite({ fresh: freshAgain, delay: 20 });
          }
        }, 60);
      }
    }, options.delay ?? 50);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleRewrite);
  } else {
    scheduleRewrite();
  }

  const schedulePostCartEventRewrite = () => scheduleRewrite({ fresh: true, delay: 80 });
  document.addEventListener("cart:update", schedulePostCartEventRewrite);
  document.addEventListener("cart:refresh", schedulePostCartEventRewrite);
  document.addEventListener("cart:updated", schedulePostCartEventRewrite);

  const scheduleFreshCartDrawerRewrite = () => {
    scheduleRewrite({ fresh: true, delay: 40 });
    setTimeout(() => scheduleRewrite({ fresh: true, delay: 40 }), 180);
    setTimeout(() => scheduleRewrite({ fresh: true, delay: 40 }), 520);
  };

  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target?.closest?.(
        '[data-testid="cart-drawer-trigger"],.header-actions__action[aria-haspopup="dialog"][aria-label*="Warenkorb"],.header-actions__action[aria-haspopup="dialog"][aria-label*="Cart"]',
      );
      if (!trigger) return;
      scheduleFreshCartDrawerRewrite();
    },
    true,
  );

  const findQuantityPricingContainerFromNode = (node) =>
    node?.closest?.("product-form-component")?.querySelector?.("[data-bitro-quantity-pricing]");

  document.addEventListener(
    "input",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.name !== "quantity") return;

      const container = findQuantityPricingContainerFromNode(target);
      if (!container) return;

      const quantity = Number.parseInt(target.value || "0", 10);
      if (!Number.isFinite(quantity) || quantity <= 0) return;

      syncProductQuantityPricing(container, quantity);
    },
    true,
  );

  document.addEventListener(
    "quantity-selector:update",
    (event) => {
      const container = findQuantityPricingContainerFromNode(event.target);
      if (!container) return;

      const quantity = Number(event.detail?.quantity || 0);
      syncProductQuantityPricing(container, quantity);
    },
    true,
  );

  document.addEventListener(
    "variant:update",
    (event) => {
      const productId = String(
        event.detail?.data?.newProduct?.id || event.detail?.data?.productId || "",
      );
      const updatedHtml = event.detail?.data?.html;
      if (!productId || !updatedHtml?.querySelector) return;

      const replacement = updatedHtml.querySelector(
        `[data-bitro-quantity-pricing][data-product-id="${productId}"]`,
      );
      const slotSelector = `[data-bitro-quantity-pricing-slot][data-product-id="${productId}"]`;
      const handledSlots = new Set();

      document
        .querySelectorAll(`[data-bitro-quantity-pricing][data-product-id="${productId}"]`)
        .forEach((container) => {
          let activeContainer = container;
          const slot = container.closest?.("[data-bitro-quantity-pricing-slot]");
          if (slot) handledSlots.add(slot);

          if (replacement) {
            const clone = replacement.cloneNode(true);
            container.replaceWith(clone);
            activeContainer = clone;
          } else {
            resetProductPriceFromUpdatedHtml(container, updatedHtml, productId);
            if (slot) {
              clearQuantityPricingSlot(slot);
            } else {
              container.remove();
            }
            return;
          }

          initializeProductQuantityPricingContainer(activeContainer);
        });

      document.querySelectorAll(slotSelector).forEach((slot) => {
        if (handledSlots.has(slot)) return;

        resetProductPriceFromUpdatedHtml(slot, updatedHtml, productId);
        clearQuantityPricingSlot(slot);

        if (!replacement) return;

        const clone = replacement.cloneNode(true);
        slot.append(clone);
        initializeProductQuantityPricingContainer(clone);
      });
    },
    true,
  );

  const initializeAllProductQuantityPricing = (scope = document) => {
    scope
      .querySelectorAll?.("[data-bitro-quantity-pricing]")
      .forEach((container) => initializeProductQuantityPricingContainer(container));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAllProductQuantityPricing(document));
  } else {
    initializeAllProductQuantityPricing(document);
  }

  const CART_MATCH_SELECTORS = [
    "#main-cart-items",
    "#main-cart-footer",
    "#cart-drawer",
    "cart-drawer",
    "cart-items",
    ".cart-drawer",
    ".cart-items",
    ".cart-form",
    ".cart-items__wrapper",
    "[data-cart-items]",
    "[data-cart-drawer]",
    "form[action='/cart']",
    "form[action$='/cart']",
  ].join(",");

  const isInCartContext = (node) => {
    if (!node) return false;
    if (node.nodeType === 1) {
      const element = node;
      if (element.matches && element.matches(CART_MATCH_SELECTORS)) return true;
      if (element.closest && element.closest(CART_MATCH_SELECTORS)) return true;
      if (element.querySelector && element.querySelector(CART_MATCH_SELECTORS)) return true;
    }
    if (node.parentElement && node.parentElement.closest) {
      return !!node.parentElement.closest(CART_MATCH_SELECTORS);
    }
    return false;
  };

  const rootObserver = new MutationObserver((mutations) => {
    if (rewriting) return;
    for (const mutation of mutations) {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "open" &&
        mutation.target?.closest?.("cart-drawer-component")
      ) {
        scheduleFreshCartDrawerRewrite();
        return;
      }
      if (isInCartContext(mutation.target)) {
        scheduleRewrite();
        return;
      }
      for (const node of mutation.addedNodes) {
        if (isInCartContext(node)) {
          scheduleRewrite();
          return;
        }
      }
    }
  });

  rootObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["open"],
  });

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = (form.getAttribute("action") || "").split("?")[0];
      if (action !== "/cart" && action !== "/checkout") return;

      const submitter = event.submitter;
      const isCheckout =
        submitter && (submitter.name === "checkout" || submitter.getAttribute("name") === "checkout");
      if (!isCheckout) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      onCheckoutClick({
        target: submitter,
        preventDefault: () => {},
        stopImmediatePropagation: () => {},
      });
    },
    true,
  );
})();
